import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { mutate } from "swr";
import useToast from "~/components/ui/toast";
import {
  NEXTGEN_CHAT_EVENT,
  trackNextgenChatEvent,
} from "~/pages/manifold-nextgen/components/conversations/analytics";
import { nextgenProjectPath } from "~/pages/manifold-nextgen/paths";
import type { PartialAgent } from "~/services/agents";
import {
  deleteConversation as apiDeleteConversation,
  generateConversationName as apiGenerateConversationName,
  updateConversation as apiUpdateConversation,
} from "~/services/conversations";
import { ProjectConversation, projectConversationsPath } from "~/services/projects";
import { isApiError, parseApiErrorBody } from "~/utils/apiClient";

// The subset of a session's fields these actions need. `ProjectConversation`
// satisfies this structurally, but callers whose data comes from elsewhere
// (e.g. the search endpoint's differently-shaped results) can pass a plain
// { id, display_name } instead of adapting their whole session type.
export interface RenameableSession {
  id: string;
  display_name: string;
  agent?: Pick<PartialAgent, "id"> | null;
}

export interface ActionTarget {
  session: RenameableSession;
  action: "share" | "delete";
}

export interface UseSessionActionsOptions {
  // Called after a rename or delete succeeds. The sidebar's list is kept in
  // sync automatically via the shared SWR cache entry below; callers backed
  // by a different data source (e.g. the sessions list page's own search
  // query) use this to trigger their own refetch.
  onMutated?: () => void;
}

// Shared rename/share/delete behavior for a project's sessions, used by both
// the sidebar's recent-sessions list and the full sessions list page so the
// two views can't drift out of sync on how they mutate the same SWR cache
// entry (`projectConversationsPath(projectId)`).
export const useSessionActions = (projectId: string, options?: UseSessionActionsOptions) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showError } = useToast();
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [autoRenamingId, setAutoRenamingId] = useState<string | null>(null);
  const [actionTarget, setActionTarget] = useState<ActionTarget | null>(null);

  const startRename = (sessionId: string) => setRenamingId(sessionId);
  const endRename = () => setRenamingId(null);

  // Writes the name into the sessions list the sidebar, breadcrumb and
  // conversation header all read, so a rename lands everywhere at once.
  const patchName = (sessionId: string, display_name: string) =>
    mutate(
      projectConversationsPath(projectId),
      (current: { items: ProjectConversation[] } | undefined) =>
        current && {
          ...current,
          items: current.items.map((s) => (s.id === sessionId ? { ...s, display_name } : s)),
        },
      { revalidate: false }
    );

  const commitRename = (session: RenameableSession, next: string) => {
    const trimmed = next.trim();
    const previous = session.display_name;
    endRename();
    if (!trimmed || trimmed === previous) {
      return;
    }
    void patchName(session.id, trimmed);
    trackNextgenChatEvent(
      NEXTGEN_CHAT_EVENT.CONVERSATION_RENAMED,
      { conversationId: session.id, projectId },
      { method: "manual" }
    );
    void apiUpdateConversation(session.id, { display_name: trimmed })
      .then(() => options?.onMutated?.())
      .catch(() => {
        void patchName(session.id, previous);
        void mutate(projectConversationsPath(projectId));
      });
  };

  // Unlike `commitRename` there is no name to show until the model returns
  // one, so this awaits the response instead of patching optimistically.
  const autoRename = async (session: RenameableSession) => {
    setAutoRenamingId(session.id);
    try {
      const renamed = await apiGenerateConversationName(session.id);
      await patchName(session.id, renamed.display_name);
      trackNextgenChatEvent(
        NEXTGEN_CHAT_EVENT.CONVERSATION_RENAMED,
        { conversationId: session.id, projectId },
        { method: "auto" }
      );
      options?.onMutated?.();
    } catch (e) {
      const body = isApiError(e) ? parseApiErrorBody(e) : null;
      showError(typeof body?.message === "string" ? body.message : "Failed to rename conversation");
    } finally {
      setAutoRenamingId(null);
    }
  };

  const confirmDelete = async () => {
    if (!actionTarget) {
      return;
    }
    const { session } = actionTarget;
    try {
      await apiDeleteConversation(session.id);
      trackNextgenChatEvent(NEXTGEN_CHAT_EVENT.CONVERSATION_DELETED, {
        conversationId: session.id,
        projectId,
      });
      await mutate(projectConversationsPath(projectId));
      options?.onMutated?.();
      if (location.pathname.includes(`/c/${session.id}`)) {
        navigate(nextgenProjectPath(projectId));
      }
    } catch (e) {
      showError("Failed to delete conversation");
      // Rethrow so the confirmation dialog stays open for a retry rather than
      // closing as though the delete had succeeded.
      throw e;
    }
  };

  return {
    renamingId,
    startRename,
    endRename,
    commitRename,
    autoRenamingId,
    autoRename,
    actionTarget,
    setActionTarget,
    confirmDelete,
  };
};
